import React from "react";
import {
  Paragraph,
  Button,
  FormControl,
  Select,
  Checkbox,
  Spinner,
  Notification,
  Textarea,
} from "@contentful/f36-components";
import { SidebarAppSDK } from "@contentful/app-sdk";
import { translateSingleEntry, pollJobUntilComplete } from "../api/backendClient";
import { updateToast, showTemporaryToast, closeToast } from "../utils/persistentToast";

interface SidebarProps {
  sdk: SidebarAppSDK;
}

const TRANSLATION_SERVICE_OPTIONS: string[] = [
  "Human Translation",
  "AI-Powered Translation + Post-Editing",
  "AI-Powered Translation"
];

const Sidebar = (props: SidebarProps) => {
  // Gets the Contentful SDK
  const { sdk } = props;

  let systemProps = sdk.entry.getSys();
  let otherLocales: string = "";
  let [sourceLanguage, setSourceLanguage] = React.useState(sdk.locales.available[0]);
  let [targetLanguages, setTargetLanguages] = React.useState<string[]>([]);
  let [shouldHideButton, setShouldHideButton] = React.useState(false);
  let [shouldHideSpinner, setShouldHideSpinner] = React.useState(true);
  let [translationService, setTranslationService] = React.useState(TRANSLATION_SERVICE_OPTIONS[0]);
  let [progressMessage, setProgressMessage] = React.useState("Submitting request...");
  let [comments, setComments] = React.useState("");

  const onSelectSourceLanguage = (langCode: string) => {
    setSourceLanguage(langCode);
  };

  const onSelectTargetLanguage = (checked: boolean, langCode: string) => {
    if (checked) {
      if (!targetLanguages.includes(langCode)) {
        setTargetLanguages([...targetLanguages, langCode]);
      }
    } else {
      setTargetLanguages(targetLanguages.filter((e) => e !== langCode));
    }
  };

  // Translated Into field value setup
  sdk.locales.available.forEach((locale, i) => {
    if (locale === sdk.locales.default) {
      return;
    }

    if (otherLocales !== "") {
      otherLocales += ", ";
    }

    otherLocales += sdk.locales.names[locale];
  });

  if (otherLocales === "") {
    otherLocales = "None";
  }

  const sendForTranslation = async () => {
    const id = systemProps.id;
    const contentType = sdk.contentType.name;

    if (!translationService) {
      Notification.setDuration(2000);
      Notification.error("Please, select a translation service.");
      return;
    }

    if (!sourceLanguage) {
      Notification.setDuration(2000);
      Notification.error("Please, select a source language.");
      return;
    }

    if (!targetLanguages || targetLanguages.length === 0) {
      Notification.setDuration(2000);
      Notification.error("Please, select at least one target language.");
      return;
    }

    if (
      targetLanguages.filter((lang) => {
        return lang === sourceLanguage;
      }).length > 0
    ) {
      Notification.setDuration(2000);
      Notification.error("The source language can not be selected as a target language.");
      return;
    }

    // Collect localized fields
    const fields: any = {};
    for (let field in sdk.entry.fields) {
      let matches = sdk.contentType.fields.filter((locField: any) => {
        return locField.localized && locField.id === field;
      });
      if (matches.length > 0) {
        fields[field] = sdk.entry.fields[field].getValue(sourceLanguage);
      }
    }

    // Shows the spinner while submitting
    setShouldHideButton(true);
    setShouldHideSpinner(false);
    setProgressMessage("Submitting request...");

    try {
      // Submit the job to backend
      const submitResult = await translateSingleEntry(
        sdk,
        id,
        contentType,
        fields,
        sourceLanguage,
        targetLanguages,
        translationService,
        comments
      );

      if (!submitResult.success || !submitResult.data?.jobId) {
        throw new Error(submitResult.error || "Failed to submit translation request");
      }

      const jobId = submitResult.data.jobId;

      // Hide spinner and re-enable button immediately so user can submit more requests
      setShouldHideButton(false);
      setShouldHideSpinner(true);
      setProgressMessage("Submitting request...");

      // Show persistent toast - same element is reused and only text changes
      updateToast("Request submitted successfully. Processing in background...", "success");

      // Poll for job completion in background (non-blocking)
      const startTime = Date.now();
      const FIVE_MINUTES = 5 * 60 * 1000;
      let hasShownLongRunningMessage = false;

      pollJobUntilComplete(jobId, (job) => {
        const elapsedTime = Date.now() - startTime;

        if (elapsedTime > FIVE_MINUTES && !hasShownLongRunningMessage) {
          hasShownLongRunningMessage = true;
          updateToast("Process is taking longer than expected. You can check the status later.", "warning");
          return;
        }

        if (job.status === 'processing' && !hasShownLongRunningMessage) {
          updateToast(`Processing translation... ${job.progress || 0}%`, "info");
        }
      }).then((completedJob) => {
        const resultCount = completedJob.result?.translatedCount || completedJob.result?.submittedCount || 0;
        const failedCount = completedJob.failedItems?.length || 0;

        if (failedCount > 0) {
          showTemporaryToast(`Translation completed with ${failedCount} failed item(s). ${resultCount} item(s) processed successfully.`, "warning", 5000);
        } else {
          showTemporaryToast(`Translation completed successfully. ${resultCount} item(s) processed.`, "success", 3000);
        }
      }).catch((error) => {
        showTemporaryToast("Translation failed: " + error.message, "error", 5000);
      });

    } catch (error: any) {
      console.error("Sidebar - Translation error:", error);
      console.error("Sidebar - Error message:", error.message);
      console.error("Sidebar - Error stack:", error.stack);
      showTemporaryToast("An error occurred while trying to submit the request. Details: " + error.message, "error", 5000);
      setShouldHideButton(false);
      setShouldHideSpinner(true);
      setProgressMessage("Submitting request...");
    }
  };

  React.useEffect(() => {
    sdk.window.startAutoResizer();
  }, [sdk.window]);

  // Final layout result
  return (
    <div>
      <FormControl className="f36-margin-bottom--m">
        <FormControl.Label>Translation Service:</FormControl.Label>
        <Select
          id="translation-service"
          name="translation-service"
          value={translationService}
          onChange={(e) => setTranslationService(e.target.value)}
        >
          {TRANSLATION_SERVICE_OPTIONS.map((option) => {
            return (
              <Select.Option value={option} key={"option-" + option}>
                {option}
              </Select.Option>
            );
          })}
        </Select>
      </FormControl>
      <FormControl>
        <FormControl.Label>From:</FormControl.Label>
        <Select
          id="sourceLanguage"
          name="sourceLanguage"
          value={sdk.locales.available[0]}
          onChange={(e) => onSelectSourceLanguage(e.target.value)}
        >
          {Object.keys(sdk.locales.names).map((key) => {
            return (
              <Select.Option value={key} key={"option-" + key}>
                {sdk.locales.names[key]}
              </Select.Option>
            );
          })}
        </Select>
      </FormControl>
      <Paragraph style={{ paddingBottom: "5px", paddingTop: "10px" }}>
        <b>To:</b>
      </Paragraph>
      {Object.keys(sdk.locales.names).map((key) => {
        return (
          <Checkbox
            key={"chk-" + key}
            id={"chk-" + key}
            name={"chk-" + key}
            isChecked={targetLanguages.includes(key)}
            onChange={(e) => onSelectTargetLanguage(e.target.checked, key)}
          >
            {sdk.locales.names[key]}
          </Checkbox>
        );
      })}
      <FormControl style={{ marginTop: "10px" }}>
        <FormControl.Label>Comments:</FormControl.Label>
        <Textarea
          id="comments"
          name="comments"
          value={comments}
          onChange={(e) => setComments(e.target.value)}
          placeholder="Add any comments or instructions..."
          rows={3}
        />
      </FormControl>
      <Button
        variant="primary"
        size="small"
        className={shouldHideButton ? "hidden-spinner" : ""}
        style={{ marginTop: "10px", marginBottom: "5px" }}
        onClick={() => sendForTranslation()}
        type="submit"
      >
        Send for Translation
      </Button>{" "}
      <div id="spinner" className={shouldHideSpinner ? "hidden-spinner" : ""}>
        <Paragraph style={{ paddingBottom: "5px", paddingTop: "10px" }}>
          <b>{progressMessage}</b> <Spinner />
        </Paragraph>
      </div>
    </div>
  );
};

export default Sidebar;
